import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * 管理者プロジェクト管理ページ（/console/projects）用 API スライス。
 *
 * - 一覧: `GET /api/admin/project-management`（MASTERADMIN のみ）
 * - メンバー一覧: `GET /api/admin/project-management/<id>/members`
 * - 削除: `DELETE /api/admin/project-management/<id>`（論理削除）
 *
 * メンバーの編集（ポジション変更・退出）はバナーと共通の
 * memberships API（`app/accounts/memberships.ts`）を使い、成功後に
 * `AdminProjectMembers` タグを無効化してモーダル内の一覧を再取得する。
 *
 * 一覧の行更新はページ側のローカル状態で行う（削除 = 行除去、
 * メンバー変更 = 行パッチ）。無限スクロールの累積をリセットしないため、
 * `AdminProjects` タグの無効化はしない。
 */

/** 一覧セル・アバタースタック用のメンバー要約（BE MemberSummarySerializer） */
export interface AdminProjectMemberSummary {
  user_id: number;
  position: string;
  display_name_jp: string;
  display_name_en: string;
  avatar_url: string | null;
  order_user: number;
  is_invited: boolean;
}

/** `GET /api/admin/project-management` の 1 行 */
export interface AdminProjectRow {
  project_id: string;
  /** 表示名（code_name 優先の表示用名称） */
  name: string;
  code_name: string;
  /** Product.last_update（ISO 文字列） */
  updated_at: string | null;
  member_count: number;
  members: AdminProjectMemberSummary[];
  /** 提出済みショット数（Product.current_scene） */
  submitted_shots_count: number;
  /** 総ショット数（Product.max_scene） */
  total_shots_count: number;
  total_message_count: number;
  dm_thread_count: number;
  /** 総費用（円、Decimal 文字列） */
  total_cost: string;
  /** ledger = 新決済 PaymentLedger 集計 / reward = 旧環境 OfferCreator.reward 集計 */
  cost_source: "ledger" | "reward";
  is_new_payment_model: boolean;
}

/** 一覧の列ヘッダーソートで指定できるキー（BE ホワイトリストと対応） */
export type AdminProjectSortField =
  | "code_name"
  | "updated_at"
  | "submitted_shots_count"
  | "total_shots_count"
  | "member_count"
  | "dm_thread_count";

export interface AdminProjectsData {
  projects: AdminProjectRow[];
  count: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface AdminProjectsResponse {
  status: string;
  data: AdminProjectsData;
}

export interface GetAdminProjectsParams {
  q?: string;
  limit?: number;
  offset?: number;
  sort?: AdminProjectSortField;
  order?: "asc" | "desc";
}

/** メンバーモーダルの 1 行（BE ProjectMembershipSerializer のフラット構造） */
export interface AdminProjectMembership {
  user_id: number;
  position: string;
  avatar_url: string | null;
  display_name_jp: string;
  display_name_en: string;
  title_jp?: string | null;
  title_en?: string | null;
  is_invited: boolean;
  order_user: number;
  /** IP制限モーダル（IpRestrictionModal）の初期値に使う */
  allowed_ips?: string[];
  [key: string]: unknown;
}

export interface AdminProjectMembersResponse {
  status: string;
  data: {
    project_id: string;
    code_name: string;
    name: string;
    count: number;
    memberships: AdminProjectMembership[];
  };
}

const buildProjectsQueryString = ({
  q,
  limit,
  offset,
  sort,
  order,
}: GetAdminProjectsParams): string => {
  const params: Record<string, string> = {};
  if (q !== undefined && q !== "") params.q = q;
  if (limit !== undefined) params.limit = String(limit);
  if (offset !== undefined) params.offset = String(offset);
  if (sort !== undefined) {
    params.sort = sort;
    if (order !== undefined) params.order = order;
  }
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : "";
};

export const adminProjectsApi = createApi({
  reducerPath: "adminProjectsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AdminProjects", "AdminProjectMembers"],
  endpoints: (builder) => ({
    /** 集計列付きプロジェクト一覧（検索・ソート・ページング） */
    getAdminProjects: builder.query<
      AdminProjectsResponse,
      GetAdminProjectsParams
    >({
      query: (params) =>
        `/api/admin/project-management${buildProjectsQueryString(params)}`,
      providesTags: (_result, _error, { q, limit, offset, sort, order }) => [
        {
          type: "AdminProjects",
          id: `${q ?? ""}:${limit ?? ""}:${offset ?? ""}:${sort ?? ""}:${order ?? ""}`,
        },
        "AdminProjects",
      ],
    }),

    /** メンバーシップモーダル用のメンバー一覧 */
    getAdminProjectMembers: builder.query<AdminProjectMembersResponse, string>({
      query: (projectId) =>
        `/api/admin/project-management/${projectId}/members`,
      providesTags: (_result, _error, projectId) => [
        { type: "AdminProjectMembers", id: projectId },
      ],
      keepUnusedDataFor: 30,
    }),

    /** プロジェクト論理削除（残高整合込み）。行はページ側でローカル除去する */
    deleteAdminProject: builder.mutation<
      { status: string; data: { project_id: string } },
      string
    >({
      query: (projectId) => ({
        url: `/api/admin/project-management/${projectId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetAdminProjectsQuery,
  useLazyGetAdminProjectsQuery,
  useGetAdminProjectMembersQuery,
  useDeleteAdminProjectMutation,
} = adminProjectsApi;
